import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Selected vehicle & tracking context
  selectedVehicle: null,
  selectedVehicleId: null,
  selectedImei: null,
  selectedTripId: null,

  // Complete fleet tracking vehicles list
  vehicles: [],

  // 1. Dynamic vehicle location updates: vehicle-location-${imeino}
  liveLocations: {}, // Map of { [imeino]: { lat, lng, speed, location, ignition, status, updatedAt } }
  currentLiveUpdate: null, // Latest socket telemetry payload

  // 2. Global fleet violation alerts: vehicle-violation-alert
  globalViolations: [], // Fleet-wide violations array
  latestGlobalViolation: null, // Newest fleet-wide alert
  totalViolationsToday: 0, // Running counter of fleet violations

  // 3. Dynamic trip violations: trip-violation-${trip_id}
  tripViolations: {}, // Map of { [trip_id]: [ ...violations ] }
  latestTripViolation: null,

  // 4. Dynamic vehicle violations: vehicle-violation-${vehicle_id}
  vehicleViolations: {}, // Map of { [vehicle_id]: [ ...violations ] }
  latestVehicleViolation: null,

  // 5. Dynamic new logs signal: new-logs-${trip_id}
  newTripLogsSignal: null, // { trip_id, timestamp, data }

  // 6. Socket health check: test
  socketHealth: {
    isConnected: false,
    lastPing: null,
    message: '',
  },
};

export const vehicleTrackingSlice = createSlice({
  name: 'vehicleTracking',
  initialState,
  reducers: {
    setSelectedTrackingVehicle: (state, action) => {
      const v = action.payload;
      state.selectedVehicle = v;
      state.selectedVehicleId = v?.id || null;
      state.selectedImei = v?.imeino || v?.imei || v?.dev || null;
      state.selectedTripId =
        v?.trip?.id || v?.trip?.trip_id || v?.detailData?.fmcr_trip?.id || null;
    },
    setTrackingVehicles: (state, action) => {
      state.vehicles = action.payload || [];
    },
    updateVehicleLocationFromSocket: (state, action) => {
      const data = action.payload;
      if (!data) return;

      const rawImei = data.imeino || data.imei || data.dev || '';
      const imeino = rawImei ? String(rawImei).trim() : '';
      const vehicleId = data.vehicle_id || data.id || null;

      let lat = data.lat != null && !isNaN(Number(data.lat)) ? Number(data.lat) : undefined;
      let lng = data.lng != null && !isNaN(Number(data.lng)) ? Number(data.lng) : undefined;

      // Coordinate sanity check: swap if reversed
      if (lat !== undefined && lng !== undefined && lat > 55 && lng < 45) {
        const swap = lat;
        lat = lng;
        lng = swap;
      }

      const speed = data.speed != null && !isNaN(Number(data.speed)) ? Number(data.speed) : undefined;
      const location = data.location || undefined;
      const ignition = data.ignition != null ? Boolean(data.ignition) : speed != null ? speed > 0 : undefined;
      const status = data.tracker_status || data.status || undefined;
      const updatedAt =
        data.created_at ||
        data.last_updated ||
        data.updated_at ||
        data.timestamp ||
        new Date().toISOString();

      const liveUpdate = {
        ...data,
        vehicle_id: vehicleId,
        imeino,
        lat,
        lng,
        speed,
        location,
        ignition,
        status,
        updatedAt,
      };

      state.currentLiveUpdate = liveUpdate;

      // Store by vehicle_id (Primary)
      if (vehicleId) {
        state.liveLocations[vehicleId] = {
          ...(state.liveLocations[vehicleId] || {}),
          ...liveUpdate,
        };
      }

      // Store by imeino (Fallback)
      if (imeino) {
        state.liveLocations[imeino] = {
          ...(state.liveLocations[imeino] || {}),
          ...liveUpdate,
        };
      }

      // Synchronize update to matching vehicle in state.vehicles list
      if (Array.isArray(state.vehicles)) {
        const vIdx = state.vehicles.findIndex(
          (v) =>
            (vehicleId && (Number(v.id) === Number(vehicleId) || Number(v.vehicle_id) === Number(vehicleId))) ||
            (imeino && (String(v.imeino).trim() === imeino || String(v.imei).trim() === imeino))
        );
        if (vIdx !== -1) {
          const v = state.vehicles[vIdx];
          let poly = v.polylineGeoJson || [];
          if (lat !== undefined && lng !== undefined && lat !== 0 && lng !== 0) {
            const lastPt = poly[poly.length - 1];
            if (!lastPt || lastPt[0] !== lng || lastPt[1] !== lat) {
              poly = [...poly, [lng, lat]];
            }
          }
          state.vehicles[vIdx] = {
            ...v,
            ...(lat !== undefined ? { lat } : {}),
            ...(lng !== undefined ? { lng } : {}),
            ...(speed !== undefined ? { spd: speed } : {}),
            ...(location && location !== '-' ? { loc: location } : {}),
            ...(ignition !== undefined ? { ignition } : {}),
            ...(status ? { st: status.toLowerCase() } : {}),
            last: new Date(updatedAt).getTime(),
            polylineGeoJson: poly,
            route: poly,
          };
        }
      }

      // If this update matches currently selected vehicle, update selectedVehicle coordinates and polyline too
      if (
        state.selectedVehicle &&
        ((vehicleId && Number(state.selectedVehicleId) === Number(vehicleId)) ||
          (imeino && String(state.selectedImei).trim() === imeino))
      ) {
        let poly = state.selectedVehicle.polylineGeoJson || [];
        if (lat !== undefined && lng !== undefined && lat !== 0 && lng !== 0) {
          const lastPt = poly[poly.length - 1];
          if (!lastPt || lastPt[0] !== lng || lastPt[1] !== lat) {
            poly = [...poly, [lng, lat]];
          }
        }
        state.selectedVehicle = {
          ...state.selectedVehicle,
          ...(lat !== undefined ? { lat } : {}),
          ...(lng !== undefined ? { lng } : {}),
          ...(speed !== undefined ? { spd: speed } : {}),
          ...(location && location !== '-' ? { loc: location } : {}),
          ...(ignition !== undefined ? { ignition } : {}),
          ...(status ? { st: status.toLowerCase() } : {}),
          last: new Date(updatedAt).getTime(),
          polylineGeoJson: poly,
          route: poly,
        };
      }
    },
    addGlobalViolationAlert: (state, action) => {
      const vio = action.payload;
      if (!vio) return;
      state.latestGlobalViolation = vio;
      state.totalViolationsToday += 1;
      state.globalViolations = [vio, ...state.globalViolations].slice(0, 100);

      // If matches currently selected vehicle, append to selectedVehicle.vio
      if (
        state.selectedVehicle &&
        ((vio.vehicle_id && Number(state.selectedVehicleId) === Number(vio.vehicle_id)) ||
          (vio.imeino && String(state.selectedImei).trim() === String(vio.imeino).trim()) ||
          (vio.reg && state.selectedVehicle.reg && String(state.selectedVehicle.reg).toLowerCase() === String(vio.reg).toLowerCase()))
      ) {
        const existing = state.selectedVehicle.vio || [];
        state.selectedVehicle = {
          ...state.selectedVehicle,
          vio: [vio, ...existing],
        };
      }
    },
    addTripViolationAlert: (state, action) => {
      const { trip_id, violation } = action.payload || {};
      if (!trip_id || !violation) return;
      state.latestTripViolation = violation;
      const key = String(trip_id);
      const existing = state.tripViolations[key] || [];
      state.tripViolations[key] = [violation, ...existing].slice(0, 50);

      // If currently selected vehicle is on this trip, append to its vio
      if (
        state.selectedVehicle &&
        (Number(state.selectedTripId) === Number(trip_id) ||
          Number(state.selectedVehicle?.trip?.id) === Number(trip_id))
      ) {
        const curVios = state.selectedVehicle.vio || [];
        state.selectedVehicle = {
          ...state.selectedVehicle,
          vio: [violation, ...curVios],
        };
      }
    },
    addVehicleViolationAlert: (state, action) => {
      const { vehicle_id, violation } = action.payload || {};
      if (!vehicle_id || !violation) return;
      state.latestVehicleViolation = violation;
      const key = String(vehicle_id);
      const existing = state.vehicleViolations[key] || [];
      state.vehicleViolations[key] = [violation, ...existing].slice(0, 50);

      // If currently selected vehicle matches, update its vio
      if (
        state.selectedVehicle &&
        Number(state.selectedVehicleId) === Number(vehicle_id)
      ) {
        const curVios = state.selectedVehicle.vio || [];
        state.selectedVehicle = {
          ...state.selectedVehicle,
          vio: [violation, ...curVios],
        };
      }
    },
    setTripNewLogsSignal: (state, action) => {
      state.newTripLogsSignal = action.payload;
    },
    setSocketHealthStatus: (state, action) => {
      state.socketHealth = {
        ...state.socketHealth,
        isConnected: true,
        lastPing: Date.now(),
        ...(action.payload || {}),
      };
    },
    clearSelectedVehicle: (state) => {
      state.selectedVehicle = null;
      state.selectedVehicleId = null;
      state.selectedImei = null;
      state.selectedTripId = null;
      state.currentLiveUpdate = null;
    },
  },
});

export const {
  setTrackingVehicles,
  setSelectedTrackingVehicle,
  updateVehicleLocationFromSocket,
  addGlobalViolationAlert,
  addTripViolationAlert,
  addVehicleViolationAlert,
  setTripNewLogsSignal,
  setSocketHealthStatus,
  clearSelectedVehicle,
} = vehicleTrackingSlice.actions;

export default vehicleTrackingSlice.reducer;

